/*
 * Enhanced Vehicles View Component
 * 
 * Personal vehicle management interface with enhanced features:
 * - Vehicle registration with owner contact information
 * - Email notifications to administrator for GPS device coordination
 * - Click-to-map navigation functionality
 * - Vehicle History feature (Traccar-like) for private vehicles only
 * - Visual indicators for vehicles with/without GPS tracking
 * - Enhanced sharing success handling with multiple feedback types
 * 
 * FEATURES TO EDIT:
 * - Vehicle registration form: Modify form fields in AddVehicleForm component
 * - Vehicle display cards: Update vehicle card layout and information
 * - Contact information fields: Add/remove owner contact fields
 * - Email notification: Customize email content sent to administrator
 * - Vehicle History: Traccar-like functionality for route tracking
 * - Success messaging: Customize success messages for sharing outcomes
 * 
 * LOCATION IN CODE: Search for "VehiclesView" function
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getTimeSinceUpdate, API } from '../../utils/config';
import AddVehicleForm from '../Forms/AddVehicleForm';
import VehicleHistory from '../Vehicle/VehicleHistory';
import VehicleShareForm from '../Vehicle/VehicleShareForm';
import VehicleShareManagement from '../Vehicle/VehicleShareManagement';
import GeofenceManagement from '../Forms/GeofenceManagement';

/**
 * Toast Notification Component
 * Quick feedback for sharing actions
 */
const ToastNotification = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000); // Auto-close after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [toast, onClose]);

  if (!toast) return null;

  const bgColor = toast.type === 'success' ? 'bg-green-500' :
    toast.type === 'invitation' ? 'bg-blue-500' : 'bg-red-500';

  return (
    <div className="fixed top-4 right-4 z-50 transform transition-all duration-300 ease-in-out">
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg max-w-md`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-2xl mr-3">
              {toast.type === 'invitation' ? '📧' : '✅'}
            </span>
            <div>
              <p className="font-semibold">{toast.title}</p>
              <p className="text-sm opacity-90">{toast.message}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-white hover:text-gray-200 text-xl"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Success Message Component
 * Displays detailed feedback for sharing outcomes
 */
const SuccessMessage = ({ message, onClose }) => {
  if (!message) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="text-center mb-4">
          <h3 className="text-xl font-bold text-gray-800 mb-2">{message.title}</h3>
          <p className="text-gray-600">{message.message}</p>
        </div>

        <div className={`border rounded-lg p-4 mb-4 ${message.type === 'invitation'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-green-50 border-green-200'
          }`}>
          <ul className={`text-sm space-y-1 ${message.type === 'invitation' ? 'text-blue-700' : 'text-green-700'
            }`}>
            {message.details.map((detail, index) => (
              <li key={index}>{detail}</li>
            ))}
          </ul>
        </div>

        <button
          onClick={onClose}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
        >
          Got it!
        </button>
      </div>
    </div>
  );
};

/**
 * Vehicle Edit Form Component
 *
 * Popup form for editing vehicle details with comprehensive validation.
 * All fields (Make, Model, Year, Color, License Plate) are required.
 *
 * For bus owners (users with is_bus_owner = true), displays additional fields:
 * - Service Type selector (Regular/Express) - only shown for bus owners AND vehicle_type = 'bus'
 * - Public visibility checkbox - only shown for bus owners AND non-private vehicles
 *
 * Features:
 * - Real-time form validation with error messages
 * - Auto-uppercase license plate input
 * - Conditional service type control for bus owners with bus vehicles
 * - Public visibility restricted to non-private vehicle types
 * - Preserves GPS device and technical settings during updates
 */
const VehicleEditForm = ({ vehicle, onClose, onSuccess, onError, currentUser }) => {
  const [formData, setFormData] = useState({
    make: vehicle.make || '',
    model: vehicle.model || '',
    year: vehicle.year || '',
    color: vehicle.color || '',
    license_plate: vehicle.license_plate || '',
    is_public: vehicle.is_public || false,
    service_option: vehicle.service_option || 'regular'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.make.trim()) {
      newErrors.make = 'Make is required';
    }
    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.year || formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = 'Valid year is required';
    }
    if (!formData.color.trim()) {
      newErrors.color = 'Color is required';
    }
    if (!formData.license_plate.trim()) {
      newErrors.license_plate = 'License plate number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API}/vehicles/${vehicle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          make: formData.make.trim(),
          model: formData.model.trim(),
          year: parseInt(formData.year),
          color: formData.color.trim(),
          license_plate: formData.license_plate.trim(),
          is_public: formData.is_public,
          // Preserve existing fields that shouldn't be changed
          gps_device_id: vehicle.gps_device_id,
          vehicle_type: vehicle.vehicle_type,
          service_option: formData.service_option,
          update_frequency: vehicle.update_frequency
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update vehicle');
      }
    } catch (error) {
      onError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-800">Edit Vehicle</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Make *
            </label>
            <input
              type="text"
              value={formData.make}
              onChange={(e) => handleInputChange('make', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.make ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., Toyota, Ford, Honda"
            />
            {errors.make && <p className="text-red-500 text-xs mt-1">{errors.make}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model *
            </label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => handleInputChange('model', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.model ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., Camry, F-150, Civic"
            />
            {errors.model && <p className="text-red-500 text-xs mt-1">{errors.model}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year *
            </label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => handleInputChange('year', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.year ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., 2020"
              min="1900"
              max={new Date().getFullYear() + 1}
            />
            {errors.year && <p className="text-red-500 text-xs mt-1">{errors.year}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color *
            </label>
            <input
              type="text"
              value={formData.color}
              onChange={(e) => handleInputChange('color', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.color ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., Red, Blue, Silver"
            />
            {errors.color && <p className="text-red-500 text-xs mt-1">{errors.color}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Plate Number *
            </label>
            <input
              type="text"
              value={formData.license_plate}
              onChange={(e) => handleInputChange('license_plate', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.license_plate ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., ABC-123"
              style={{ textTransform: 'uppercase' }}
            />
            {errors.license_plate && <p className="text-red-500 text-xs mt-1">{errors.license_plate}</p>}
          </div>

          {/* Service Type and Public Visibility - Bus Owners Only */}
          {currentUser?.is_bus_owner && (
            <>
              {/* Service Type selector - only for bus/truck vehicles */}
              {(vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Type
                  </label>
                  <select
                    value={formData.service_option}
                    onChange={(e) => handleInputChange('service_option', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="regular">Regular</option>
                    <option value="express">Express</option>
                  </select>
                </div>
              )}

              {/* Public visibility checkbox - only for non-private vehicles */}
              {vehicle.vehicle_type !== 'private' && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_public}
                      onChange={(e) => handleInputChange('is_public', e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Make this vehicle publicly available (others can see its location)
                    </span>
                  </label>
                </div>
              )}
            </>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-md text-white transition-colors ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                }`}
            >
              {isSubmitting ? 'Updating...' : 'Update Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Enhanced Vehicles View Component
 * Personal vehicle management with click-to-map functionality and vehicle history
 */
// A GPS device is considered offline if we haven't heard from it (a position
// update OR a heartbeat) within this window.
const DEVICE_OFFLINE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

// Parse a backend timestamp to epoch ms. Backend sends naive UTC without a
// trailing 'Z', so append one before parsing.
const parseServerTs = (ts) => {
  if (!ts) return 0;
  let s = String(ts);
  if (s.includes('T') && !s.endsWith('Z') && !s.includes('+') && !s.includes('-', 10)) {
    s += 'Z';
  }
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Most recent contact: newer of last GPS position (last_updated) and last
// received packet incl. heartbeat (device_last_seen).
const getLastContactMs = (vehicle) =>
  Math.max(parseServerTs(vehicle?.last_updated), parseServerTs(vehicle?.device_last_seen));

const isDeviceOnline = (vehicle) => {
  const last = getLastContactMs(vehicle);
  return last > 0 && (Date.now() - last) <= DEVICE_OFFLINE_THRESHOLD_MS;
};

const VehiclesView = ({ vehicles, onRefresh, onVehicleClick, enableSwipe, disableSwipe, currentUser }) => {
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [selectedVehicleForHistory, setSelectedVehicleForHistory] = useState(null);
  const [selectedVehicleForSharing, setSelectedVehicleForSharing] = useState(null);
  const [selectedVehicleForManagement, setSelectedVehicleForManagement] = useState(null);
  const [selectedVehicleForEdit, setSelectedVehicleForEdit] = useState(null);
  const [selectedVehicleForGeofence, setSelectedVehicleForGeofence] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toast, setToast] = useState(null);
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [geofenceCounts, setGeofenceCounts] = useState({});
  const [shutdownStates, setShutdownStates] = useState({}); // Track shutdown state for each vehicle
  const [shutdownConfirmation, setShutdownConfirmation] = useState(null); // Confirmation dialog state

  const anyModalOpen =
    showRegistrationForm ||
    selectedVehicleForHistory ||
    selectedVehicleForSharing ||
    selectedVehicleForManagement ||
    selectedVehicleForEdit ||
    selectedVehicleForGeofence ||
    successMessage ||
    shutdownConfirmation;

  useEffect(() => {
    if (!enableSwipe || !disableSwipe) return;   // parent didn't pass helpers
    anyModalOpen ? disableSwipe() : enableSwipe();
  }, [anyModalOpen, enableSwipe, disableSwipe]);

  // Reusable function to fetch geofence counts
  const fetchGeofenceCounts = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token || vehicles.length === 0) return;

    const counts = {};

    // Fetch geofence counts for each vehicle
    await Promise.all(
      vehicles.map(async (vehicle) => {
        try {
          const response = await fetch(
            `${API}/geofences?vehicle_id=${vehicle.id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.ok) {
            const geofences = await response.json();
            counts[vehicle.id] = geofences.length;
          } else {
            counts[vehicle.id] = 0;
          }
        } catch (error) {
          counts[vehicle.id] = 0;
        }
      })
    );

    setGeofenceCounts(counts);
  }, [vehicles]);

  // Fetch geofence counts when vehicles change
  useEffect(() => {
    fetchGeofenceCounts();
  }, [fetchGeofenceCounts]);

  // Initialize shutdown states from vehicles' engine_shutdown field
  useEffect(() => {
    if (vehicles && vehicles.length > 0) {
      const states = {};
      vehicles.forEach(vehicle => {
        // Initialize from vehicle's engine_shutdown field (True = shutdown, False = running)
        states[vehicle.id] = vehicle.engine_shutdown || false;
      });
      setShutdownStates(states);
    }
  }, [vehicles]);

  const NotificationTest = () => {
    const { testBackgroundNotification } = useVehicleTracker();

    return (
      <div className="p-4 bg-yellow-100 border border-yellow-400 rounded">
        <h3 className="font-bold">🧪 Notification Test</h3>
        <button
          onClick={testBackgroundNotification}
          className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
        >
          Test Background Notification
        </button>
        <p className="text-sm mt-2">
          Click test, then minimize the app to check if system notification appears.
        </p>
      </div>
    );
  };

  const handleVehicleClick = (vehicle) => {
    //
    if (vehicle.last_location && onVehicleClick) {
      //
      onVehicleClick(vehicle);
    } else {
    }
  };

  const handleShowHistory = (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click
    //
    setSelectedVehicleForHistory(vehicle);
  };

  const handleShowSharing = (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click
    //
    setSelectedVehicleForSharing(vehicle);
    setSharingInProgress(false); // Reset sharing state
  };

  const handleManageSharing = (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click
    //
    setSelectedVehicleForManagement(vehicle);
  };

  const handleEditVehicle = (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click
    //
    setSelectedVehicleForEdit(vehicle);
  };

  const handleManageGeofences = (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click
    //
    setSelectedVehicleForGeofence(vehicle);
  };

  const handleToggleActive = async (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const newActiveStatus = !vehicle.is_active;

      const response = await fetch(`${API}/vehicles/${vehicle.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: newActiveStatus
        })
      });

      if (response.ok) {
        setToast({
          type: 'success',
          title: newActiveStatus ? 'Vehicle Activated' : 'Vehicle Deactivated',
          message: newActiveStatus ? 'Vehicle will show on map' : 'Vehicle hidden from map'
        });
        onRefresh(); // Refresh vehicle list
      } else {
        throw new Error('Failed to update vehicle status');
      }
    } catch (error) {
      setToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Unable to update vehicle status'
      });
    }
  };

  const handleShutdownVehicle = async (vehicle, event) => {
    event.stopPropagation(); // Prevent triggering the main vehicle click

    // Check if vehicle has GPS device
    if (!vehicle.gps_device_id) {
      setToast({
        type: 'error',
        title: 'No GPS Device',
        message: 'This vehicle does not have a GPS device configured for remote commands'
      });
      return;
    }

    // Show confirmation dialog
    const isCurrentlyShutdown = shutdownStates[vehicle.id] || false;
    setShutdownConfirmation({
      vehicle,
      isCurrentlyShutdown
    });
  };

  const executeShutdownCommand = async () => {
    if (!shutdownConfirmation) return;

    const { vehicle, isCurrentlyShutdown } = shutdownConfirmation;
    setShutdownConfirmation(null); // Close dialog

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const commandType = isCurrentlyShutdown ? 'engineResume' : 'engineStop';

      // Send command to GPS device via Traccar-like API
      const response = await fetch(`${API}/commands/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: vehicle.gps_device_id,
          type: commandType,
          description: isCurrentlyShutdown ? 'Resume engine' : 'Stop engine'
        })
      });

      if (response.ok) {
        // Update local shutdown state immediately for responsiveness
        setShutdownStates(prev => ({
          ...prev,
          [vehicle.id]: !isCurrentlyShutdown
        }));

        setToast({
          type: 'success',
          title: isCurrentlyShutdown ? 'Engine Resumed' : 'Engine Shutdown',
          message: isCurrentlyShutdown
            ? 'Command sent to resume vehicle engine'
            : 'Command sent to shutdown vehicle engine'
        });

        // Refresh vehicles list to get updated engine_shutdown status from database
        if (onRefresh) {
          setTimeout(() => onRefresh(), 500); // Small delay to ensure DB update completes
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send command to GPS device');
      }
    } catch (error) {
      setToast({
        type: 'error',
        title: 'Command Failed',
        message: error.message || 'Unable to send shutdown command to vehicle'
      });
    }
  };

  /**
   * Enhanced Share Success Handler
   * Handles both invitation and normal sharing scenarios with multiple feedback types
   */
  const handleShareSuccess = (result) => {
    //
    setSharingInProgress(false);

    if (result.invitation_sent) {
      // Show immediate toast notification for invitation
      setToast({
        type: 'invitation',
        title: 'Invitation Sent!',
        message: `Email sent to ${result.recipient_email}`
      });

      // Show detailed success message
      setSuccessMessage({
        type: 'invitation',
        title: '📧 Invitation Sent Successfully!',
        message: `An invitation has been sent to ${result.recipient_email}. They'll receive an email with registration instructions and can access your vehicle once they create an account.`,
        details: [
          '• The recipient will get a detailed email with vehicle information',
          '• They can register using the provided link',
          '• Once registered, they will automatically have access to your vehicle',
          '• You can manage this share from your vehicle dashboard'
        ]
      });
    } else {
      // Show immediate toast notification for normal sharing
      setToast({
        type: 'success',
        title: 'Vehicle Shared!',
        message: `Shared with ${result.recipient_email || 'user'}`
      });

      // Show detailed success message
      setSuccessMessage({
        type: 'share',
        title: '✅ Vehicle Shared Successfully!',
        message: `Your vehicle has been shared with ${result.recipient_email || 'the user'}. They now have access according to your schedule settings.`,
        details: [
          '• The user will receive a notification if enabled',
          '• They can now see your vehicle on their map',
          '• Sharing is active according to your schedule',
          '• You can modify or remove sharing anytime'
        ]
      });
    }

    setSelectedVehicleForSharing(null);
    // Refresh vehicle list to update share counts
    onRefresh();
  };

  /**
   * Handle sharing form submission start
   */
  const handleShareStart = () => {
    setSharingInProgress(true);
  };

  /**
   * Handle sharing form errors
   */
  const handleShareError = (error) => {
    setSharingInProgress(false);
    setToast({
      type: 'error',
      title: 'Sharing Failed',
      message: error.message || 'Unable to share vehicle'
    });
  };

  const isPrivateVehicle = (vehicle) => {
    return !vehicle.is_public && vehicle.vehicle_type !== 'bus' && vehicle.vehicle_type !== 'taxi';
  };

  const isBusVehicle = (vehicle) => {
    return vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Vehicles</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your registered vehicles and GPS tracking devices
          </p>
        </div>
        <button
          onClick={() => setShowRegistrationForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          Add New Vehicle
        </button>
      </div>

      {vehicles.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🚗</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No vehicles registered yet</h3>
          <p className="text-gray-600 mb-4">
            Register your first vehicle to start GPS tracking
          </p>
          <p className="text-sm text-gray-500">
            Click "Add New Vehicle" to get started with GPS device coordination
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => {
            const isShutdown = shutdownStates[vehicle.id] || false;

            return (
            <div
              key={vehicle.id}
              onClick={() => handleVehicleClick(vehicle)}
              className={`border rounded-lg p-6 transition-all duration-200 ${
                isShutdown
                  ? 'bg-red-50 border-red-300 hover:shadow-lg cursor-pointer'
                  : vehicle.last_location
                    ? 'hover:shadow-lg hover:scale-105 cursor-pointer bg-blue-50 border-blue-200'
                    : 'bg-gray-50 border-gray-200 cursor-not-allowed'
                }`}
              style={{ cursor: vehicle.last_location ? 'pointer' : 'not-allowed' }}
            >
              {/* Vehicle Header */}
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-semibold text-lg text-gray-900">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h3>
                <div className="flex flex-col items-end space-y-1">
                  {isDeviceOnline(vehicle) ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                      🟢 Online
                    </span>
                  ) : vehicle.last_location || vehicle.last_updated ? (
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                      🔴 Offline
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      📍 No GPS Data
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs rounded-full ${vehicle.is_public
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    }`}>
                    {vehicle.is_public ? 'Public' : 'Private'}
                  </span>
                  {vehicle.is_public && (
                    <span className={`px-2 py-1 text-xs rounded-full ${vehicle.is_active === false
                        ? 'bg-gray-200 text-gray-700'
                        : 'bg-green-100 text-green-800'
                      }`}>
                      {vehicle.is_active === false ? '🙈 Hidden' : '👁️ Visible'}
                    </span>
                  )}
                  {isShutdown && (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs rounded-full font-medium">
                      🔒 Engine Shutdown
                    </span>
                  )}
                </div>
              </div>

              {/* Vehicle Details */}
              <div className="space-y-2 text-sm text-gray-600">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="font-medium text-gray-800">License:</p>
                    <p>{vehicle.license_plate}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Color:</p>
                    <p>{vehicle.color}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="font-medium text-gray-800">Type:</p>
                    <p className="capitalize">{vehicle.vehicle_type}</p>
                  </div>
                  {/* <div>
                    <p className="font-medium text-gray-800">Updates:</p>
                    <p>{vehicle.update_frequency}s</p>
                  </div> */}
                </div>

                {/* Service Option for Bus Vehicles */}
                {isBusVehicle(vehicle) && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="font-medium text-gray-800">Service:</p>
                      <p className="capitalize">
                        <span className={`px-2 py-1 text-xs rounded-full ${vehicle.service_option === 'express'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                          }`}>
                          {vehicle.service_option || 'Regular'}
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Last Location Info */}
                {vehicle.last_location && (
                  <div className="pt-2 border-t border-gray-200">
                    <p className="font-medium text-gray-800">Last Update:</p>
                    <p className="text-xs">{getTimeSinceUpdate(vehicle.last_updated)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {vehicle.last_location.latitude?.toFixed(4)}, {vehicle.last_location.longitude?.toFixed(4)}
                    </p>
                  </div>
                )}
              </div>

              {/* Click Action Indicator & History Button */}
              <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                {vehicle.last_location && (
                  <p className="text-xs text-blue-600 font-medium flex items-center">
                    <span className="mr-2">🗺️</span>
                    Click to view on map
                  </p>
                )}

                {/* Vehicle History Button - Private Vehicles Only */}
                {/* {isPrivateVehicle(vehicle) && ( */}
                <button
                  onClick={(e) => handleShowHistory(vehicle, e)}
                  className="w-full flex items-center justify-center px-3 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors text-xs font-medium mb-2"
                >
                  <span className="mr-2">📊</span>
                  View Vehicle History
                </button>
                {/* )} */}

                {/* Vehicle Sharing Button - Private Vehicles Only */}
                {isPrivateVehicle(vehicle) && (
                  <button
                    onClick={(e) => handleShowSharing(vehicle, e)}
                    disabled={sharingInProgress}
                    className={`w-full flex items-center justify-center px-3 py-2 rounded-md transition-colors text-xs font-medium mb-2 ${sharingInProgress
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                  >
                    <span className="mr-2">
                      {sharingInProgress ? '⏳' : '🤝'}
                    </span>
                    {sharingInProgress ? 'Sharing...' : 'Share Vehicle'}
                  </button>
                )}

                {/* Manage Sharing Button - Private Vehicles Only */}
                {isPrivateVehicle(vehicle) && (
                  <button
                    onClick={(e) => handleManageSharing(vehicle, e)}
                    className="w-full flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-xs font-medium"
                  >
                    <span className="mr-2">👥</span>
                    Manage Sharing
                    {vehicle.share_count > 0 && (
                      <span className="ml-2 px-2 py-1 bg-blue-600 text-white rounded-full text-xs">
                        {vehicle.share_count}
                      </span>
                    )}
                  </button>
                )}

                {/* Geofences Button - All Vehicles */}
                <button
                  onClick={(e) => handleManageGeofences(vehicle, e)}
                  className="w-full flex items-center justify-center px-3 py-2 bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 transition-colors text-xs font-medium mb-2"
                >
                  <span className="mr-2">🗺️</span>
                  Geofences
                  {geofenceCounts[vehicle.id] > 0 && (
                    <span className="ml-2 px-2 py-1 bg-indigo-600 text-white rounded-full text-xs">
                      {geofenceCounts[vehicle.id]}
                    </span>
                  )}
                </button>

                {/* Edit Vehicle Button - All Vehicles */}
                <button
                  onClick={(e) => handleEditVehicle(vehicle, e)}
                  className="w-full flex items-center justify-center px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors text-xs font-medium mb-2"
                >
                  <span className="mr-2">✏️</span>
                  Edit Vehicle
                </button>

                {/* Shutdown Vehicle Button - Vehicles with GPS Device and Remote Shutdown enabled */}
                {vehicle.gps_device_id && vehicle.has_remote_shutdown && (
                  <button
                    onClick={(e) => handleShutdownVehicle(vehicle, e)}
                    className={`w-full flex items-center justify-center px-3 py-2 rounded-md transition-colors text-xs font-medium mb-2 ${
                      shutdownStates[vehicle.id]
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    <span className="mr-2">
                      {shutdownStates[vehicle.id] ? '🔓' : '🔒'}
                    </span>
                    {shutdownStates[vehicle.id] ? 'Resume Vehicle' : 'Shutdown Vehicle'}
                  </button>
                )}

                {/* Toggle Active/Inactive Button - All Vehicles */}
                <button
                  onClick={(e) => handleToggleActive(vehicle, e)}
                  className={`w-full flex items-center justify-center px-3 py-2 rounded-md transition-colors text-xs font-medium ${vehicle.is_active === false
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                >
                  <span className="mr-2">
                    {vehicle.is_active === false ? '👁️' : '🙈'}
                  </span>
                  {vehicle.is_active === false ? 'Show on Map' : 'Hide on Map'}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Vehicle Registration Form Modal */}
      {showRegistrationForm && (
        <AddVehicleForm
          onClose={() => setShowRegistrationForm(false)}
          onSubmit={() => {
            onRefresh();
            setShowRegistrationForm(false);
          }}
        />
      )}

      {/* Vehicle History Modal */}
      {selectedVehicleForHistory && (
        <VehicleHistory
          vehicle={selectedVehicleForHistory}
          onClose={() => setSelectedVehicleForHistory(null)}
        />
      )}

      {/* Vehicle Share Form Modal */}
      {selectedVehicleForSharing && (
        <VehicleShareForm
          vehicle={selectedVehicleForSharing}
          onClose={() => {
            setSelectedVehicleForSharing(null);
            setSharingInProgress(false);
          }}
          onSuccess={handleShareSuccess}
          onStart={handleShareStart}
          onError={handleShareError}
        />
      )}

      {/* Vehicle Share Management Modal */}
      {selectedVehicleForManagement && (
        <VehicleShareManagement
          vehicle={selectedVehicleForManagement}
          onClose={() => setSelectedVehicleForManagement(null)}
          onUpdate={() => {
            // Refresh the vehicle list to update share counts
            onRefresh();
          }}
        />
      )}

      {selectedVehicleForEdit && (
        <VehicleEditForm
          vehicle={selectedVehicleForEdit}
          currentUser={currentUser}  // Pass current user for bus owner check
          onClose={() => setSelectedVehicleForEdit(null)}
          onSuccess={() => {
            setSelectedVehicleForEdit(null);
            onRefresh(); // Refresh the vehicle list
            setToast({
              type: 'success',
              title: 'Success!',
              message: 'Vehicle updated successfully'
            });
          }}
          onError={(error) => {
            setToast({
              type: 'error',
              title: 'Update Failed',
              message: error.message || 'Unable to update vehicle'
            });
          }}
        />
      )}

      {/* Geofence Management Modal */}
      {selectedVehicleForGeofence && (
        <GeofenceManagement
          vehicle={selectedVehicleForGeofence}
          currentUser={currentUser}
          onClose={() => {
            setSelectedVehicleForGeofence(null);
            // Refresh geofence counts when modal closes
            fetchGeofenceCounts();
          }}
        />
      )}

      {/* Enhanced Success Message Modal */}
      <SuccessMessage
        message={successMessage}
        onClose={() => setSuccessMessage(null)}
      />

      {/* Toast Notification */}
      <ToastNotification
        toast={toast}
        onClose={() => setToast(null)}
      />

      {/* Shutdown Confirmation Dialog */}
      {shutdownConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">
                {shutdownConfirmation.isCurrentlyShutdown ? '🔓' : '⚠️'}
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                {shutdownConfirmation.isCurrentlyShutdown ? 'Resume Engine?' : 'Shutdown Engine?'}
              </h3>
              <p className="text-gray-600 mb-2">
                {shutdownConfirmation.vehicle.year} {shutdownConfirmation.vehicle.make} {shutdownConfirmation.vehicle.model}
              </p>
              <p className="text-sm text-gray-500">
                License: {shutdownConfirmation.vehicle.license_plate}
              </p>
            </div>

            <div className={`border rounded-lg p-4 mb-4 ${
              shutdownConfirmation.isCurrentlyShutdown
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm ${
                shutdownConfirmation.isCurrentlyShutdown
                  ? 'text-green-800'
                  : 'text-red-800'
              }`}>
                {shutdownConfirmation.isCurrentlyShutdown
                  ? 'This will send a command to resume the vehicle engine. The vehicle will be able to start normally.'
                  : 'This will send a command to remotely shutdown the vehicle engine. The vehicle will not be able to start until you resume the engine.'}
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShutdownConfirmation(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeShutdownCommand}
                className={`flex-1 px-4 py-2 rounded-md text-white transition-colors ${
                  shutdownConfirmation.isCurrentlyShutdown
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {shutdownConfirmation.isCurrentlyShutdown ? 'Resume Engine' : 'Shutdown Engine'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesView;